import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { formatDate, getImageUrl } from '../../utils/helpers';
import PrivateImage from '../PrivateImage/PrivateImage';
import { UserIcon, EnvelopeIcon, IdentificationIcon, CalendarIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
var userEmail;
var JSToken;
var accessToken;
if (localStorage.user) {
  userEmail = localStorage.user_email;
  JSToken = JSON.parse(localStorage.user);
  accessToken = JSToken.access_token;
}
const config = {
  headers: { Authorization: `Bearer ${accessToken}` },
};


const UserProfileCard = ({ delegateEmail }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `https://dash.bestdiplomats.org/api/method/get-delegate-profile-info?delegate_email=${delegateEmail}`,
          config
        );
        if (response.status===200) {
          setProfile(response.data.data[0]);
        } else {
          setError(response.error);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (delegateEmail) {
      fetchProfile();
    }
  }, [delegateEmail]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-blue-500 to-blue-600" />
        <div className="px-6 pb-6">
          <div className="relative -mt-16 mb-4">
            <Skeleton circle width={128} height={128} />
          </div>
          <Skeleton height={30} width={200} className="mb-2" />
          <Skeleton height={20} width={150} className="mb-4" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton height={80} />
            <Skeleton height={80} />
            <Skeleton height={80} />
            <Skeleton height={80} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-red-600">Error loading profile: {error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header Banner */}
      <div className="h-32 bg-gradient-to-r from-blue-500 to-blue-600 relative">
        <div className="absolute top-4 right-4">
          <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
            Delegate Profile
          </span>
        </div>
      </div>

      {/* Profile Content */}
      <div className="px-6 pb-6">
        {/* Profile Picture */}
        <div className="relative -mt-16 mb-4">
          <div className="inline-block">
            <PrivateImage 
              imageUrl={profile?.profile_picture}
              className="h-32 w-32 rounded-full border-4 border-white shadow-lg object-cover"
            />
            <div className="absolute bottom-2 right-2 h-6 w-6 bg-green-500 rounded-full border-4 border-white"></div>
          </div>
        </div>

        {/* Name and Email */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            {profile?.full_name}
          </h2>
          {localStorage.role !== "Operations Team" && (
            <p className="text-gray-600 flex items-center gap-2">
              <EnvelopeIcon className="h-4 w-4" />
              {profile?.email}
            </p>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          
          {/* Registration Date */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CalendarIcon className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 font-medium mb-1">Member Since</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatDate(profile?.registration_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Total Conferences */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 font-medium mb-1">Conferences Attended</p>
                <p className="text-2xl font-bold text-gray-900">
                  {profile?.total_conferences || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Country */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <GlobeAltIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 font-medium mb-1">Country</p>
                <p className="text-sm font-semibold text-gray-900">
                  {profile?.country || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* User ID */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <IdentificationIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 font-medium mb-1">Contact Number</p>
                <p className="text-sm font-semibold text-gray-900 break-all">
                  {profile?.phone}
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserProfileCard;